import Typography from '@mui/material/Typography';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import { analyticsViewItem } from 'lib/util/analytics';
import {
  selectFeatures,
  selectHeaderDescriptionText,
  selectIsPMP,
} from 'lib/util/privateMarketplace';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ReduxState } from 'ducks';
import { markProductVisited } from 'ducks/client/visitedProducts';

import { ProductDetailsView } from './ProductDetailsView';
import { selectBookingWidgetHeaderDescription } from 'lib/util/bookingWidgetDesign';
import { useQueryString } from 'hooks/useQueryString';

export const ProductDetails = () => {
  const { t } = useTranslation();
  const { apiKey, useApiKeyInPaths } = React.useContext(ApiKeyContext);
  const [hasScrolled100Px, setHasScrolled100px] = React.useState<boolean>(false);

  const dispatch = useDispatch();

  const productListEnabled = useSelector(
    (state: ReduxState) => state.server.settings.all.enable_product_list_page
  );
  const product = useSelector((state: ReduxState) => state.server.product.product);
  const features = useSelector(selectFeatures);

  const bookingWidgetHeaderDescription = useSelector(selectBookingWidgetHeaderDescription);
  const pmpHeaderDescription = useSelector(selectHeaderDescriptionText);
  const isPMP = useSelector(selectIsPMP);
  const settings = useSelector((state: ReduxState) => state.server.settings.all);

  const headerDescription = isPMP ? pmpHeaderDescription : bookingWidgetHeaderDescription;

  const title = headerDescription || settings.supplier_name;

  React.useEffect(() => {
    if (product) {
      analyticsViewItem([
        {
          id: `${product.id}`,
          name: `[${product.supplier_reference}] ${product.product_name}`,
        },
      ]);

      dispatch(markProductVisited(product.id));
    }
  }, [product]);

  React.useEffect(() => {
    const scrollListener = () => {
      const scrolled = document.body.scrollTop || document.documentElement.scrollTop;

      setHasScrolled100px(scrolled > 100);
    };
    window.addEventListener('scroll', scrollListener);

    return () => window.removeEventListener('scroll', scrollListener);
  }, []);

  const configQueryString = useQueryString();

  if (!product) {
    return (
      <div>
        <Typography variant="h5" component="h3">
          {t('Product not found')}
        </Typography>
      </div>
    );
  }

  const productFeatures = [
    ...features.filter((feature) => (feature.product_ids || []).includes(product.id)),
  ].map((c) => c.name);

  let productListPath = useApiKeyInPaths ? `/${apiKey}` : '/';
  if (configQueryString) {
    productListPath = `${productListPath}?${configQueryString}`;
  }

  return (
    <ProductDetailsView
      product={product}
      productListPath={productListPath}
      shouldShowBookNowButton={hasScrolled100Px}
      features={productFeatures}
      productListEnabled={productListEnabled}
      pageTitle={title}
      headerDescription={headerDescription}
      logoUrl={settings.logo_url}
    />
  );
};
